import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, LogOut, QrCode, BarChart3, Crown, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../utils/api';
import { clearAuthData, getAuthData } from '../utils/auth';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Logo from '../components/Logo';
import QRCodeCard from '../components/QRCodeCard';
import BulkQRUpload from '../components/BulkQRUpload';

const DashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    totalScans: 0,
  });
  const { user } = getAuthData();

  const fetchQRCodes = useCallback(async () => {
    try {
      const response = await api.get('/qr');
      setQrCodes(response.data);
      const total = response.data.length;
      const active = response.data.filter(qr => qr.isActive).length;
      const totalScans = response.data.reduce((acc, qr) => acc + (qr.scanCount || 0), 0);
      setStats({ total, active, totalScans });
    } catch (error) {
      toast.error(t('error_occurred'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchQRCodes();
  }, [fetchQRCodes]);

  const handleLogout = () => {
    clearAuthData();
    navigate('/login');
    toast.success(t('logout'));
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/qr/${id}`);
      toast.success(t('qr_deleted'));
      fetchQRCodes();
    } catch (error) {
      toast.error(t('error_occurred'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-primary-50 to-white">
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/20 shadow-sm">
        <div className="container mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2" data-testid="dashboard-header">
            <Logo className="h-8 w-8" />
            <span className="text-2xl font-heading font-bold text-primary">cyQRus</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:block" data-testid="company-name">{user?.companyName}</span>
            {user?.isAdmin && (
              <Link to="/admin">
                <Button variant="ghost" size="sm" className="text-purple-600 hover:text-purple-700">
                  <Shield className="h-4 w-4 mr-2" />
                  Admin
                </Button>
              </Link>
            )}
            <Link to="/pricing">
              <Button variant="ghost" size="sm" className="text-accent">
                <Crown className="h-4 w-4 mr-2" />
                Upgrade
              </Button>
            </Link>
            <LanguageSwitcher />
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-2" />
              {t('logout')}
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 md:px-12 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-primary" data-testid="dashboard-title">{t('dashboard')}</h1>
          <div className="flex gap-3">
            <BulkQRUpload onSuccess={fetchQRCodes} />
            <Link to="/create" data-testid="create-qr-btn">
              <Button className="btn-accent">
                <Plus className="h-5 w-5 mr-2" />
                {t('create_qr')}
              </Button>
            </Link>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-6 mb-12">
          <div className="card p-6 space-y-2" data-testid="stat-total-qr">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{t('total_qr_codes')}</p>
              <QrCode className="h-5 w-5 text-accent" />
            </div>
            <p className="text-3xl font-heading font-bold text-primary">{stats.total}</p>
          </div>
          <div className="card p-6 space-y-2" data-testid="stat-active-codes">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{t('active_codes')}</p>
              <BarChart3 className="h-5 w-5 text-accent" />
            </div>
            <p className="text-3xl font-heading font-bold text-primary">{stats.active}</p>
          </div>
          <div className="card p-6 space-y-2" data-testid="stat-total-scans">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{t('total_scans')}</p>
              <BarChart3 className="h-5 w-5 text-accent" />
            </div>
            <p className="text-3xl font-heading font-bold text-primary">{stats.totalScans}</p>
          </div>
        </div>

        <div className="mb-6">
          <h2 className="text-2xl font-heading font-semibold text-primary mb-4" data-testid="my-qr-codes-title">{t('my_qr_codes')}</h2>
        </div>

        {loading ? (
          <div className="text-center py-12" data-testid="loading-spinner">
            <p className="text-muted-foreground">{t('loading')}</p>
          </div>
        ) : qrCodes.length === 0 ? (
          <div className="text-center py-12 card" data-testid="empty-state">
            <QrCode className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">{t('my_qr_codes')}</p>
            <Link to="/create">
              <Button className="btn-accent">{t('create_qr')}</Button>
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="qr-codes-grid">
            {qrCodes.map((qr) => (
              <QRCodeCard key={qr.id} qrCode={qr} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default DashboardPage;
